import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert as MuiAlert,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Tooltip,
  Divider,
  Stack
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ScienceIcon from "@mui/icons-material/Science";
import FocusedFlowPage from "components/FocusedFlowPage";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "constants/routes";
import { getEncounterPlan, registerNewBatch } from "utils/apiBiospecimen";

export default function EncounterPlan() {
  const { encounterId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [planData, setPlanData] = useState(null);
  const [error, setError] = useState("");

  // Register batch modal
  const [openRegister, setOpenRegister] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [activeStudyForRegister, setActiveStudyForRegister] = useState(null);
  const [kitType, setKitType] = useState("");
  const [kitNumber, setKitNumber] = useState("");
  const [registering, setRegistering] = useState(false);

  // Snackbar for error messages
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const plan = await getEncounterPlan(encounterId);
      setPlanData(plan);
    } catch (err) {
      console.error(err);
      setError("Failed to load collection plan for this encounter.");
    } finally {
      setLoading(false);
    }
  }, [encounterId]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const handleRegisterBatch = async () => {
    if (!kitType || !kitNumber || !activeStudyForRegister) return;
    setRegistering(true);
    try {
      await registerNewBatch(encounterId, kitType, kitNumber, [activeStudyForRegister.irbStudy]);
      setOpenRegister(false);
      setKitType("");
      setKitNumber("");
      setActiveStudyForRegister(null);
      fetchPlan();
      setSnackbar({ open: true, message: "Kit applied successfully!", severity: "success" });
    } catch (err) {
      console.error("Error applying kit:", err);

      // Extract error message from various possible error formats
      let errorMessage = "Failed to apply kit. Please ensure the kit exists and is not already assigned.";

      if (err.response?.data) {
        // Backend returned structured error
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      } else if (err.message && typeof err.message === 'string') {
        errorMessage = err.message;
      }

      setSnackbar({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setRegistering(false);
    }
  };

  const handleBatchClick = (batchId) => {
    // Find the batch data
    const batch = uniqueBatches.find(b => b.batchId === batchId);

    // Prepare encounterData to pass to wizard
    const encounterData = {
      encounter: planData?.encounter,
      patient: planData?.patient,
      batch: batch,
      eligibility: planData?.eligibility,
      studies: planData?.studies,
    };

    // Navigate to the new wizard flow (ReviewCollectionDetails)
    navigate(`/biobank/sample-collection/encounters/${encounterId}/batches/${batchId}/review`, {
      state: { encounterData }
    });
  };

  const onClose = () => {
    navigate(BIOBANK_ROUTES.SAMPLE_COLLECTION);
  };

  const handleOpenRegister = (study) => {
    setActiveStudyForRegister(study);

    // Check if study has incomplete batches
    if (study.hasIncompleteBatch || study.recommendedAction === 'COMPLETE_EXISTING_FIRST') {
      setOpenWarning(true);
    } else {
      setOpenRegister(true);
    }
  };

  const handleConfirmWarning = () => {
    setOpenWarning(false);
    setOpenRegister(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get all unique batches across all studies
  const getAllUniqueBatches = () => {
    if (!planData?.studies) return [];

    const batchMap = new Map();
    planData.studies.forEach(study => {
      study.existingBatches?.forEach(batch => {
        if (!batchMap.has(batch.batchId)) {
          batchMap.set(batch.batchId, batch);
        }
      });
    });

    return Array.from(batchMap.values());
  };

  const uniqueBatches = getAllUniqueBatches();

  if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 10 }} />;

  return (
    <FocusedFlowPage title="Sample Collection Plan" onClose={onClose}>
      <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
        {error && <Typography color="error">{error}</Typography>}

        {planData && (
          <Box>
            {/* Module 1: Active Collection Kits */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: "#500000" }}>
                Active Collection Kits
              </Typography>

              {uniqueBatches.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: "center", bgcolor: "#f5f5f5" }}>
                  <Typography color="textSecondary">
                    No kits assigned yet. Apply a kit to a study below to get started.
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2 }}>
                  {uniqueBatches.map((batch) => {
                    const progress = batch.progress || { expected: 0, collected: 0, percent: 0 };
                    return (
                      <Tooltip key={batch.batchId} title="Click to start collection" arrow>
                        <Card
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.2s",
                            position: "relative",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: 4
                            }
                          }}
                        >
                          <CardActionArea onClick={() => handleBatchClick(batch.batchId)}>
                            <CardContent>
                              {/* Status chip in top right corner */}
                              <Box sx={{ position: "absolute", top: 12, right: 12 }}>
                                <Chip
                                  label={batch.isComplete ? "Completed" : "Incomplete"}
                                  color={batch.isComplete ? "success" : "warning"}
                                  size="small"
                                />
                              </Box>

                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <ScienceIcon color="primary" />
                                <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                                  {batch.batchId}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="textSecondary">
                                Progress: {progress.collected} / {progress.expected}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Tooltip>
                    );
                  })}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Module 2: Study Kit Management */}
            <Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: "#500000" }}>
                Study Kit Management
              </Typography>

              {(!planData.studies || planData.studies.length === 0) ? (
                <Typography variant="body1">No active studies found for this encounter.</Typography>
              ) : (
                <Stack spacing={2}>
                  {planData.studies.map((study, index) => (
                    <Paper key={study.irbStudy || index} sx={{ p: 2 }} elevation={1}>
                      <Box>
                        {/* Study name on first line */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          {study.irbStudy}: {study.studyName || "Unknown Study"}
                        </Typography>

                        {/* Allowed Kits, Active Kits, and Apply button on second line */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                            {/* Allowed Kit Types */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" color="textSecondary">
                                Allowed Kit Types:
                              </Typography>
                              {study.allowedKitTypes?.map(kt => (
                                <Chip key={kt} label={kt} size="small" variant="outlined" />
                              ))}
                            </Box>

                            {/* Active Kits */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" color="textSecondary">
                                Active Kits:
                              </Typography>
                              {study.existingBatches && study.existingBatches.length > 0 ? (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {study.existingBatches.map(b => b.batchId).join(", ")}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                                  None
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Apply Kit Button */}
                          <Tooltip title="Apply a new kit" arrow>
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenRegister(study)}
                              sx={{
                                "&:hover": {
                                  bgcolor: "primary.light",
                                  color: "white"
                                }
                              }}
                            >
                              <AddCircleOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Bottom button to exit/complete */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{ minWidth: 180 }}
              >
                Complete Collection
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Warning Dialog for Incomplete Batches */}
      <Dialog open={openWarning} onClose={() => setOpenWarning(false)}>
        <DialogTitle>Incomplete Batches Warning</DialogTitle>
        <DialogContent>
          <Typography>
            This study has incomplete batches. It is recommended to complete existing batches first before applying a new kit.
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 600 }}>
            Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWarning(false)}>Cancel</Button>
          <Button onClick={handleConfirmWarning} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Kit Dialog */}
      <Dialog open={openRegister} onClose={() => setOpenRegister(false)}>
        <DialogTitle>Apply a Kit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Applying kit for Study: {activeStudyForRegister?.irbStudy}
          </Typography>
          <TextField
            select
            fullWidth
            label="Kit Type"
            value={kitType}
            onChange={(e) => setKitType(e.target.value)}
            margin="normal"
          >
            {activeStudyForRegister?.allowedKitTypes?.map((kt) => (
              <MenuItem key={kt} value={kt}>
                {kt}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Kit Number"
            value={kitNumber}
            onChange={(e) => setKitNumber(e.target.value)}
            margin="normal"
            placeholder="Enter 5-digit kit number"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRegister(false)} disabled={registering}>Cancel</Button>
          <Button onClick={handleRegisterBatch} variant="contained" disabled={registering || !kitType || !kitNumber}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </FocusedFlowPage>
  );
}
